/**
 * URL helpers shared by the pagination builder and the history manager.
 */

/**
 * Derives the canonical base URL of the current page, stripping any trailing `/page/N` segment so
 * pagination links and history entries build from a stable root.
 *
 * @param {boolean} endingSlash Whether the returned URL should end with a slash.
 * @returns {string}
 */
export function getBaseUrl(endingSlash = false) {
    let baseURL = location.protocol + "//" + location.hostname + location.pathname;
    const pagePos = baseURL.search("/page/");

    if(pagePos > 0) {
        baseURL = baseURL.substring(0, pagePos);
    }

    if(!endingSlash && baseURL.endsWith("/")) {
        baseURL = baseURL.substring(0, baseURL.length - 1);
    } else if(endingSlash && !baseURL.endsWith("/")) {
        baseURL += "/";
    }

    return baseURL;
}

/**
 * Escapes `*` → %2A in an already-encoded URL component, for GTM passthrough/linker URLs (which treat `*` as
 * a delimiter). `*` is the one character neither the browser nor {@link buildHash}'s encodeURIComponent
 * escapes, so it's the only thing left to handle here.
 *
 * Deliberately does NOT run encodeURI: the input is already percent-encoded (from `location.search`/
 * `location.hash`, or from buildHash), and encodeURI re-encodes existing `%` to `%25`, double-encoding the
 * value (a search "a b" → "a%2520b") so it only half-decodes on the next load and lands still-encoded in the
 * search box.
 *
 * @param {string} str An already-encoded URL component (query or fragment, without its leading `?`/`#`).
 * @returns {string}
 */
export function escapeForGtm(str) {
    return str.replace(/\*/g, "%2A");
}

/**
 * Serializes the URL-sync fragment for a shortcode's state. Format (slug-driven, path-style):
 *   #/<keySlug>/<val>[|<val>]/<keySlug2>/<val>/q=<search>
 * Each key slug is followed by its `|`-joined value slugs; the free-text search (when present) is a trailing
 * `q=<encoded>` segment. Segments are component-encoded so `/`, `|`, spaces in a search term can't break
 * parsing. The `@<form-id>` scroll marker is NEVER written here — it's a manual, opt-in hint only.
 *
 * @param {object} data `{ params: { keySlug: string[] }, search?: string }`
 * @returns {string} e.g. `#/product/trustradius|salesforce/q=crm%20tools` (empty string when nothing active).
 */
export function buildHash({ params = {}, search = "" } = {}) {
    const parts = [];

    for(const [key, vals] of Object.entries(params)) {
        const values = (Array.isArray(vals) ? vals : [vals]).filter((v) => v !== "" && v != null);
        if(!values.length) continue;

        parts.push(encodeURIComponent(key));
        parts.push(values.map((v) => encodeURIComponent(String(v))).join("|"));
    }

    if(search) {
        parts.push("q=" + encodeURIComponent(String(search)));
    }

    return parts.length ? "#/" + parts.join("/") : "";
}

/**
 * Parses a URL-sync fragment back into `{ params: { keySlug: string[] }, search, scroll }`. `q=`/`@` marker
 * segments are pulled out regardless of position; the remainder are read as alternating key / value(s) pairs
 * (values split on `|`). A dangling key with no value is ignored, so a plain `#anchor` yields no params.
 *
 * @param {string} hash The raw `location.hash` (with or without a leading `#`/`#/`).
 * @returns {{params: Object<string,string[]>, search: string, scroll: (string|null)}}
 */
export function parseHash(hash) {
    const out = { params: {}, search: "", scroll: null };
    if(!hash) return out;

    let body = String(hash).replace(/^#\/?/, "").replace(/\/$/, "");
    if(!body) return out;

    const pairs = [];
    for(const seg of body.split("/")) {
        if(seg === "") continue;
        if(seg.startsWith("q=")) { out.search = decodeURIComponent(seg.slice(2)); continue; }
        if(seg.startsWith("@")) { out.scroll = decodeURIComponent(seg.slice(1)); continue; }
        pairs.push(seg);
    }

    for(let i = 0; i + 1 < pairs.length; i += 2) {
        const key = decodeURIComponent(pairs[i]);
        const values = pairs[i + 1].split("|").map((v) => decodeURIComponent(v)).filter(Boolean);
        if(values.length) out.params[key] = values;
    }

    return out;
}
